import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { businessAPI } from '../services/api';
import { openExternalWithNotice } from '../utils/externalLinks';
import { formatMarathiNumber, toMarathiDigits } from '../utils/marathi';
import { phoneHref } from '../utils/phone';
import './Businesses.css';
import { directionsUrl, GoogleLocationMap } from '../components/maps/LocationMap';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Icon = ({ name }) => {
  const paths = {
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
    grid: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>,
    list: <><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></>,
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.2 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.32 1.77.59 2.6a2 2 0 0 1-.45 2.11L8 9.68a16 16 0 0 0 6.32 6.32l1.25-1.25a2 2 0 0 1 2.11-.45c.83.27 1.7.47 2.6.59A2 2 0 0 1 22 16.92Z" />,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    info: <><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></>,
    map: <><path d="M12 21s7-4.7 7-11a7 7 0 1 0-14 0c0 6.3 7 11 7 11Z" /><circle cx="12" cy="10" r="2.5" /></>,
    calendar: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
    badge: <><path d="m12 3 2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5-3.6-3.5 5-.7L12 3Z" /></>,
    close: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    store: <><path d="M4 10h16l-1-6H5l-1 6Z" /><path d="M6 10v10h12V10" /><path d="M10 20v-6h4v6" /></>,
    sprout: <><path d="M12 20V9" /><path d="M12 9c-4.5 0-7-2.5-7-6 4.5 0 7 2.5 7 6Z" /><path d="M12 13c4.5 0 7-2.5 7-6-4.5 0-7 2.5-7 6Z" /></>,
    building: <><path d="M4 21V7l8-4 8 4v14" /><path d="M9 21v-6h6v6" /><path d="M8 9h.01M12 9h.01M16 9h.01" /></>,
  };
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.store}</svg>;
};

const businessTypes = [
  { value: 'all', label: 'सर्व', icon: 'grid' },
  { value: 'agriculture', label: 'कृषी', icon: 'sprout' },
  { value: 'dairy', label: 'डेअरी', icon: 'store' },
  { value: 'livestock', label: 'पशुपालन', icon: 'store' },
  { value: 'retail', label: 'किरकोळ विक्री', icon: 'store' },
  { value: 'grocery', label: 'किराणा', icon: 'store' },
  { value: 'restaurant', label: 'रेस्टॉरंट', icon: 'store' },
  { value: 'hotel', label: 'हॉटेल', icon: 'building' },
  { value: 'transportation', label: 'वाहतूक', icon: 'store' },
  { value: 'medical', label: 'औषधालय', icon: 'store' },
  { value: 'education', label: 'शिक्षण', icon: 'building' },
  { value: 'manufacturing', label: 'उत्पादन', icon: 'building' },
  { value: 'services', label: 'सेवा', icon: 'store' },
  { value: 'handicrafts', label: 'हस्तकला', icon: 'store' },
  { value: 'tourism', label: 'पर्यटन', icon: 'map' },
  { value: 'other', label: 'इतर', icon: 'store' },
];

const typeMeta = (type) => businessTypes.find(item => item.value === type) || businessTypes[businessTypes.length - 1];
const trimText = (value, limit = 84) => {
  const text = String(value || '').trim();
  return text.length > limit ? `${text.slice(0, limit)}...` : text;
};

export default function Businesses() {
  const { tenantSlug } = useParams();
  const [businesses, setBusinesses] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await businessAPI.getPublic(tenantSlug);
        setBusinesses(response.data || []);
      } catch (err) {
        console.error('Error fetching businesses:', err);
        setError('व्यवसाय माहिती लोड करण्यात अडचण आली.');
      } finally {
        setLoading(false);
      }
    };

    if (tenantSlug) fetchBusinesses();
  }, [tenantSlug]);

  const filteredBusinesses = useMemo(() => {
    const text = search.trim().toLowerCase();
    return businesses.filter(item => {
      const matchesType = selectedType === 'all' || item.business_type === selectedType;
      const haystack = [
        item.business_name,
        item.business_type,
        item.business_category,
        item.products_services,
        item.phone,
        item.address,
      ].filter(Boolean).join(' ').toLowerCase();
      return matchesType && (!text || haystack.includes(text));
    });
  }, [businesses, selectedType, search]);

  const counts = useMemo(() => businessTypes.reduce((acc, item) => {
    acc[item.value] = item.value === 'all'
      ? businesses.length
      : businesses.filter(business => business.business_type === item.value).length;
    return acc;
  }, {}), [businesses]);

  const selectedMeta = selectedBusiness ? typeMeta(selectedBusiness.business_type) : null;
  const selectedWebsite = selectedBusiness?.website && /^https?:\/\//i.test(selectedBusiness.website)
    ? selectedBusiness.website
    : selectedBusiness?.website
      ? `https://${selectedBusiness.website}`
      : '';
  const selectedPrimaryLocation = selectedBusiness?.locations?.find(location => location.is_primary)
    || selectedBusiness?.locations?.[0];
  const selectedMapAddress = selectedBusiness?.address
    || [selectedPrimaryLocation?.address_line_1, selectedPrimaryLocation?.locality, selectedPrimaryLocation?.pincode].filter(Boolean).join(', ');
  const selectedLatitude = selectedBusiness?.latitude ?? selectedPrimaryLocation?.latitude;
  const selectedLongitude = selectedBusiness?.longitude ?? selectedPrimaryLocation?.longitude;
  const selectedMapUrl = directionsUrl(selectedLatitude, selectedLongitude, selectedMapAddress);

  return (
    <div className="biz-page">
      <nav className="biz-breadcrumb">
        <Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link><span>›</span><span>व्यवसाय</span>
      </nav>

      <section className="biz-hero">
        <div>
          <span>स्थानिक अर्थव्यवस्था</span>
          <h1>व्यवसाय निर्देशिका</h1>
          <p>ग्रामपंचायत कार्यक्षेत्रातील पडताळलेले व्यवसाय, सेवा, सार्वजनिक संपर्क आणि ठिकाणांची माहिती.</p>
        </div>
      </section>

      <div className="biz-layout">
        <aside className="biz-sidebar">
          <h2>व्यवसाय प्रकार</h2>
          <div className="biz-filter-list">
            {businessTypes.filter(item => item.value === 'all' || counts[item.value] > 0).map(item => (
              <button
                type="button"
                key={item.value}
                className={selectedType === item.value ? 'active' : ''}
                onClick={() => setSelectedType(item.value)}
              >
                <span><Icon name={item.icon} /> {item.label}</span>
                <b>{formatMarathiNumber(counts[item.value] || 0)}</b>
              </button>
            ))}
          </div>
        </aside>

        <main className="biz-content-card">
          <div className="biz-toolbar">
            <div>
              <h2>व्यवसाय यादी</h2>
              <p>{formatMarathiNumber(filteredBusinesses.length)} व्यवसाय दाखवत आहे</p>
            </div>
            <label className="biz-search">
              <Icon name="search" />
              <input value={search} onChange={event => setSearch(event.target.value)} placeholder="व्यवसाय, सेवा किंवा ठिकाण शोधा..." />
            </label>
            <div className="biz-view-toggle">
              <button type="button" className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')}><Icon name="grid" /> ग्रिड</button>
              <button type="button" className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}><Icon name="list" /> लिस्ट</button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner fullPage message="व्यवसाय माहिती लोड होत आहे..." />
          ) : error ? (
            <div className="biz-empty error">{error}</div>
          ) : filteredBusinesses.length === 0 ? (
            <div className="biz-empty">या फिल्टरसाठी व्यवसाय सापडले नाहीत.</div>
          ) : (
            <div className={`biz-grid ${view === 'list' ? 'list' : ''}`}>
              {filteredBusinesses.map((business, index) => {
                const meta = typeMeta(business.business_type);
                const publicLocation = business.locations?.find(location => location.is_primary) || business.locations?.[0];
                return (
                  <article
                    key={business.id}
                    className={selectedBusiness?.id === business.id ? 'biz-card active' : 'biz-card'}
                    onClick={() => setSelectedBusiness(business)}
                  >
                    <div className="biz-card-head">
                      <span className="biz-number">{formatMarathiNumber(index + 1)}</span>
                      <span className="biz-icon"><Icon name={meta.icon} /></span>
                      <div>
                        <h3>{business.business_name}</h3>
                        <small>{meta.label}{business.business_category ? ` · ${business.business_category}` : ''}</small>
                      </div>
                    </div>
                    <div className="biz-card-info">
                      {publicLocation && <p><Icon name="map" /> {[publicLocation.locality, publicLocation.ward_number].filter(Boolean).join(', ') || 'स्थानिक व्यवसाय'}</p>}
                      {business.phone && <a href={phoneHref(business.phone)} onClick={event => event.stopPropagation()}><Icon name="phone" /> {business.phone}</a>}
                      {business.establishment_year && <p><Icon name="calendar" /> {toMarathiDigits(business.establishment_year)}</p>}
                      <p><Icon name="info" /> {trimText(business.products_services || business.description || 'व्यवसाय तपशील उपलब्ध नाही.')}</p>
                    </div>
                    <div className="biz-card-actions">
                      <button type="button" onClick={(event) => { event.stopPropagation(); setSelectedBusiness(business); }}>संपूर्ण माहिती</button>
                      {business.phone && <a href={phoneHref(business.phone)} onClick={event => event.stopPropagation()}>संपर्क करा</a>}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {selectedBusiness && (
        <div className="biz-drawer-backdrop" onClick={() => setSelectedBusiness(null)}>
          <aside className="biz-drawer" onClick={event => event.stopPropagation()}>
            <div className="biz-drawer-top">
              <button type="button" onClick={() => setSelectedBusiness(null)}><Icon name="arrow" /> मागे जा</button>
              <button type="button" aria-label="बंद करा" onClick={() => setSelectedBusiness(null)}><Icon name="close" /></button>
            </div>

            <header className="biz-drawer-hero">
              <span className="biz-drawer-index">{formatMarathiNumber(filteredBusinesses.findIndex(item => item.id === selectedBusiness.id) + 1 || 1)}</span>
              <span className="biz-drawer-icon"><Icon name={selectedMeta.icon} /></span>
              <div>
                <h2>{selectedBusiness.business_name}</h2>
                <p>{selectedMeta.label}{selectedBusiness.business_category ? ` · ${selectedBusiness.business_category}` : ''}</p>
                <div className="biz-status-row">
                  {selectedBusiness.is_verified && <span className="verified">सत्यापित</span>}
                  {selectedBusiness.is_active && <span className="active">सक्रिय</span>}
                </div>
              </div>
            </header>

            <div className="biz-drawer-body">
              <section>
                <h3>मूलभूत माहिती</h3>
                <dl>
                  <div><dt>व्यवसाय नाव</dt><dd>{selectedBusiness.business_name}</dd></div>
                  <div><dt>व्यवसाय प्रकार</dt><dd>{selectedMeta.label}</dd></div>
                  {selectedBusiness.business_category && <div><dt>व्यवसाय वर्ग</dt><dd>{selectedBusiness.business_category}</dd></div>}
                  {selectedBusiness.establishment_year && <div><dt>स्थापना वर्ष</dt><dd>{toMarathiDigits(selectedBusiness.establishment_year)}</dd></div>}
                </dl>
              </section>

              <section>
                <h3>संपर्क माहिती</h3>
                <dl>
                  {selectedBusiness.phone && <div><dt>फोन नंबर</dt><dd><a href={phoneHref(selectedBusiness.phone)}><Icon name="phone" /> {selectedBusiness.phone}</a></dd></div>}
                  {selectedBusiness.email && <div><dt>ईमेल</dt><dd><a href={`mailto:${selectedBusiness.email}`}><Icon name="mail" /> {selectedBusiness.email}</a></dd></div>}
                  {selectedWebsite && <div><dt>वेबसाइट</dt><dd><a href={selectedWebsite} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, selectedWebsite)}>{selectedBusiness.website}</a></dd></div>}
                  {selectedBusiness.address && <div><dt>दुकान / कार्यालयाचा पत्ता</dt><dd><Icon name="map" /> {selectedBusiness.address}</dd></div>}
                </dl>
              </section>

              {(selectedBusiness.products_services || selectedBusiness.description) && (
                <section>
                  <h3>व्यवसाय माहिती</h3>
                  {selectedBusiness.products_services && <p><strong>उत्पादन / सेवा:</strong> {selectedBusiness.products_services}</p>}
                  {selectedBusiness.description && <p>{selectedBusiness.description}</p>}
                </section>
              )}

              {(selectedBusiness.opening_hours || selectedBusiness.locations?.length > 0) && <section>
                <h3>सार्वजनिक ठिकाणे व वेळ</h3>
                <dl>
                  {selectedBusiness.opening_hours && <div><dt>वेळ</dt><dd>{toMarathiDigits(selectedBusiness.opening_hours)}</dd></div>}
                  {(selectedBusiness.locations || []).map(location => <div key={location.location_uid}><dt>{location.branch_name || (location.is_primary ? 'प्राथमिक ठिकाण' : 'ठिकाण')}</dt><dd>{[location.address_line_1, location.address_line_2, location.locality, location.ward_number, location.pincode].filter(Boolean).join(', ')}</dd></div>)}
                </dl>
              </section>}
              <GoogleLocationMap latitude={selectedLatitude} longitude={selectedLongitude} title={selectedBusiness.business_name} address={selectedMapAddress} />
            </div>

            <footer className="biz-drawer-actions">
              {selectedBusiness.phone && <a href={phoneHref(selectedBusiness.phone)}><Icon name="phone" /> संपर्क करा</a>}
              {selectedMapUrl && <a href={selectedMapUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, selectedMapUrl)}><Icon name="map" /> दिशा मिळवा</a>}
            </footer>
          </aside>
        </div>
      )}
    </div>
  );
}
